const ENDPOINT = '/apps/sigma-stock-alerts/bundles/v1/mystery';
const MARKER_PARAMETER = 'filter.p.m.custom.fixed_pack_stock_mode';
const MARKER_VALUE = 'private_bag_hold';
const CANDIDATE_SECTION_ID = 'mystery-availability-candidates';
const MAX_QUANTITY = 5;
const MAX_CONCURRENT_QUOTES = 3;
const REQUEST_TIMEOUT_MS = 8_000;

const rootStates = new WeakMap();
const activeRoots = new Set();

const isNumericId = (value) => /^\d+$/.test(String(value || ''));

export const buildMysteryCandidateUrl = (currentUrl, renderedThemeId = '') => {
  const url = new URL(currentUrl.toString());
  url.searchParams.delete('filter.v.availability');
  url.searchParams.delete('show_sold_out');
  url.searchParams.delete('page');
  url.searchParams.set(MARKER_PARAMETER, MARKER_VALUE);
  url.searchParams.set('section_id', CANDIDATE_SECTION_ID);
  return url;
};

export const responseProvesMarkerFilter = ({ present, active, value, pages } = {}) => (
  present === true
  && active === true
  && value === MARKER_VALUE
  && Number(pages) === 1
);

export const readMysteryQuote = (payload) => {
  const data = payload?.data;
  const maxQuantity = data?.maxQuantity;
  if (
    payload?.ok !== true
    || data?.mode !== MARKER_VALUE
    || typeof data?.available !== 'boolean'
    || !Number.isInteger(maxQuantity)
    || maxQuantity < 0
    || maxQuantity > MAX_QUANTITY
    || (data.available && maxQuantity < 1)
    || (!data.available && maxQuantity !== 0)
  ) return null;

  return { available: data.available, maxQuantity };
};

const createLimiter = (limit) => {
  let active = 0;
  const waiting = [];

  const advance = () => {
    if (active >= limit || waiting.length === 0) return;
    const { task, resolve, reject } = waiting.shift();
    active += 1;
    Promise.resolve()
      .then(task)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        advance();
      });
  };

  return (task) => new Promise((resolve, reject) => {
    waiting.push({ task, resolve, reject });
    advance();
  });
};

const requestWithTimeout = async (url, { signal, accept, read }) => {
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(() => timeout.abort(), REQUEST_TIMEOUT_MS);
  const abort = () => timeout.abort();
  if (signal.aborted) timeout.abort();
  signal.addEventListener('abort', abort, { once: true });

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: accept },
      signal: timeout.signal,
    });
    return { response, body: await read(response) };
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener('abort', abort);
  }
};

const setCardState = (card, state, message) => {
  if (!(card instanceof HTMLElement)) return;
  const status = card.querySelector('[data-private-mystery-status]');
  const retry = card.querySelector('[data-private-mystery-retry]');
  const predictiveStock = card.querySelector('[data-predictive-search-stock]');
  const predictiveCompact = card.querySelector('[data-predictive-search-stock-compact]');

  card.dataset.mysteryState = state;
  card.dataset.productAvailability = state === 'available' ? 'available' : 'checking';
  card.classList.remove('product-card--sold-out');
  card.toggleAttribute('aria-busy', state === 'checking');
  if (status instanceof HTMLElement) status.textContent = message;
  if (predictiveStock instanceof HTMLElement) {
    predictiveStock.dataset.stockState = state;
    predictiveStock.setAttribute('aria-label', message);
  }
  if (predictiveCompact instanceof HTMLElement) {
    predictiveCompact.textContent = state === 'available'
      ? 'Available'
      : state === 'checking'
        ? 'Checking…'
        : 'Unavailable';
  }
  if (retry instanceof HTMLButtonElement) retry.hidden = state === 'checking' || state === 'available';
};

const requestQuote = async (variantId, state, { refresh = false } = {}) => {
  if (!isNumericId(variantId)) return null;
  if (!refresh && state.quotePromises.has(variantId)) return state.quotePromises.get(variantId);

  const quotePromise = state.limitQuotes(async () => {
    if (state.controller.signal.aborted) return null;
    const { response, body } = await requestWithTimeout(
      `${ENDPOINT}?variant_id=${encodeURIComponent(variantId)}`,
      {
        signal: state.controller.signal,
        accept: 'application/json',
        read: (quoteResponse) => quoteResponse.json(),
      },
    );
    if (!response.ok) return null;
    return readMysteryQuote(body);
  }).catch(() => null);

  state.quotePromises.set(variantId, quotePromise);
  return quotePromise;
};

const stateIsCurrent = (root, state) => (
  Boolean(state)
  && root.isConnected
  && !state.controller.signal.aborted
  && rootStates.get(root) === state
);

const quoteExistingCard = async (root, state, card, { refresh = false } = {}) => {
  const variantId = card.dataset.privateMysteryVariantId || '';
  setCardState(card, 'checking', 'Checking today’s availability…');
  const quote = await requestQuote(variantId, state, { refresh });
  if (!stateIsCurrent(root, state) || !card.isConnected) return;

  if (quote?.available) {
    setCardState(card, 'available', 'Available today');
  } else if (quote) {
    setCardState(card, 'unavailable', 'This coffee box is unavailable right now.');
  } else {
    setCardState(card, 'error', 'We couldn’t check this coffee box. Try again.');
  }
};

const groupElements = (root) => ({
  group: root.querySelector('[data-mystery-availability-group]'),
  grid: root.querySelector('[data-mystery-availability-grid]'),
  count: root.querySelector('[data-mystery-availability-count]'),
  announcement: root.querySelector('[data-mystery-availability-announcement]'),
});

const showGroup = (root, insertedCount) => {
  const { group, count, announcement } = groupElements(root);
  if (!(group instanceof HTMLElement)) return;
  if (insertedCount < 1) {
    group.hidden = true;
    return;
  }
  const label = `${insertedCount} coffee ${insertedCount === 1 ? 'box' : 'boxes'} available`;
  group.hidden = false;
  if (count instanceof HTMLElement) count.textContent = label;
  if (announcement instanceof HTMLElement) announcement.textContent = `${label} today.`;
};

const updateGroupCount = (root) => {
  const { grid } = groupElements(root);
  if (!(grid instanceof HTMLElement)) return;
  showGroup(root, grid.querySelectorAll(':scope > [data-private-mystery-card]').length);
};

const applyBrowsingMode = (root, card) => {
  const mode = root.dataset.collectionBrowsingMode === 'advanced' ? 'advanced' : 'basic';
  card.dataset.productCardMode = mode;
  const advanced = mode === 'advanced';
  card.querySelectorAll('[data-product-advanced-facts], [data-product-advanced-reveal]').forEach((panel) => {
    if (!(panel instanceof HTMLElement)) return;
    panel.inert = !advanced;
    panel.setAttribute('aria-hidden', String(!advanced));
  });
  const basic = card.querySelector('[data-product-basic-summary]');
  if (basic instanceof HTMLElement) {
    basic.inert = advanced;
    basic.setAttribute('aria-hidden', String(advanced));
  }
};

const rootHasNativeProduct = (root, productId) => (
  [...root.querySelectorAll(`[data-private-mystery-product-id="${productId}"]`)]
    .some((card) => !card.closest('[data-mystery-availability-grid]'))
);

const readCandidate = async (root, state, card, seenProducts) => {
  const productId = card.dataset.privateMysteryProductId || '';
  const variantId = card.dataset.privateMysteryVariantId || '';
  if (
    !isNumericId(productId)
    || !isNumericId(variantId)
    || seenProducts.has(productId)
    || rootHasNativeProduct(root, productId)
  ) return null;
  seenProducts.add(productId);

  const quote = await requestQuote(variantId, state);
  if (!stateIsCurrent(root, state) || !quote?.available || rootHasNativeProduct(root, productId)) return null;
  return card;
};

const discoverCandidates = async (root, state, seenProducts) => {
  if (root.dataset.mysteryDiscovery !== 'candidates') return 0;
  const { response, body } = await requestWithTimeout(
    buildMysteryCandidateUrl(new URL(window.location.href), root.dataset.mysteryThemeId || ''),
    {
      signal: state.controller.signal,
      accept: 'text/html',
      read: (candidateResponse) => candidateResponse.text(),
    },
  );
  if (!response.ok || !stateIsCurrent(root, state)) return 0;

  const documentResponse = new DOMParser().parseFromString(body, 'text/html');
  const candidateResponse = documentResponse.querySelector('[data-mystery-candidate-response]');
  if (!(candidateResponse instanceof HTMLElement)) return 0;
  const proof = {
    present: candidateResponse.dataset.markerFilterPresent === 'true',
    active: candidateResponse.dataset.markerFilterActive === 'true',
    value: candidateResponse.dataset.markerFilterValue || '',
    pages: Number(candidateResponse.dataset.candidatePages || 0),
  };
  if (!responseProvesMarkerFilter(proof)) return 0;

  const cards = [...candidateResponse.querySelectorAll('[data-private-mystery-card]')]
    .filter((card) => card instanceof HTMLElement);
  const quotedCards = await Promise.all(cards.map((card) => readCandidate(root, state, card, seenProducts)));
  if (!stateIsCurrent(root, state)) return 0;
  const { grid } = groupElements(root);
  if (!(grid instanceof HTMLElement)) return 0;
  let insertedCount = 0;
  quotedCards.forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    setCardState(card, 'available', 'Available today');
    applyBrowsingMode(root, card);
    grid.append(card);
    insertedCount += 1;
  });
  return insertedCount;
};

const startRoot = async (root) => {
  if (!(root instanceof HTMLElement)) return;
  rootStates.get(root)?.controller.abort();

  const state = {
    controller: new AbortController(),
    limitQuotes: createLimiter(MAX_CONCURRENT_QUOTES),
    quotePromises: new Map(),
  };
  rootStates.set(root, state);
  activeRoots.add(root);

  const { group, grid } = groupElements(root);
  if (grid instanceof HTMLElement) grid.replaceChildren();
  if (group instanceof HTMLElement) group.hidden = true;

  const existingCards = [...root.querySelectorAll('[data-private-mystery-card]')]
    .filter((card) => card instanceof HTMLElement && !card.closest('[data-mystery-availability-grid]'));
  const seenProducts = new Set(existingCards.map((card) => card.dataset.privateMysteryProductId || ''));
  const grouped = root.dataset.mysteryGrouped === 'true';
  let groupedExistingCount = 0;

  const existingWork = existingCards.map(async (card) => {
    card.dataset.mysteryHydrated = 'true';
    if (!grouped) return quoteExistingCard(root, state, card);
    card.hidden = true;
    const quote = await requestQuote(card.dataset.privateMysteryVariantId || '', state);
    if (!stateIsCurrent(root, state) || !quote?.available || !(grid instanceof HTMLElement)) return;
    setCardState(card, 'available', 'Available today');
    applyBrowsingMode(root, card);
    card.hidden = false;
    grid.append(card);
    groupedExistingCount += 1;
  });

  const discoveryWork = discoverCandidates(root, state, seenProducts).catch(() => 0);
  const [, insertedCount] = await Promise.all([Promise.all(existingWork), discoveryWork]);
  if (!stateIsCurrent(root, state)) return;
  showGroup(root, groupedExistingCount + insertedCount);
};

const hydrateNewCards = (root) => {
  const state = rootStates.get(root);
  if (!stateIsCurrent(root, state)) return;
  const { grid } = groupElements(root);
  const grouped = root.dataset.mysteryGrouped === 'true';
  const cards = [...root.querySelectorAll('[data-private-mystery-card]')]
    .filter((card) => (
      card instanceof HTMLElement
      && !card.closest('[data-mystery-availability-grid]')
      && card.dataset.mysteryHydrated !== 'true'
    ));

  cards.forEach((card) => {
    card.dataset.mysteryHydrated = 'true';
    const productId = card.dataset.privateMysteryProductId || '';
    if (isNumericId(productId) && grid instanceof HTMLElement) {
      grid.querySelector(`[data-private-mystery-product-id="${productId}"]`)?.remove();
      updateGroupCount(root);
    }

    if (!grouped) {
      quoteExistingCard(root, state, card);
      return;
    }

    card.hidden = true;
    requestQuote(card.dataset.privateMysteryVariantId || '', state).then((quote) => {
      if (!stateIsCurrent(root, state) || !quote?.available || !(grid instanceof HTMLElement)) return;
      setCardState(card, 'available', 'Available today');
      applyBrowsingMode(root, card);
      card.hidden = false;
      grid.append(card);
      updateGroupCount(root);
    });
  });
};

const initializeRoot = (root) => {
  if (!(root instanceof HTMLElement)) return;
  if (root.dataset.mysteryAvailabilityInitialized === 'true') return;
  root.dataset.mysteryAvailabilityInitialized = 'true';
  root.addEventListener('click', (event) => {
    const retry = event.target.closest('[data-private-mystery-retry]');
    const card = retry?.closest('[data-private-mystery-card]');
    const state = rootStates.get(root);
    if (!(retry instanceof HTMLButtonElement) || !(card instanceof HTMLElement) || !state) return;
    event.preventDefault();
    state.quotePromises.delete(card.dataset.privateMysteryVariantId || '');
    quoteExistingCard(root, state, card, { refresh: true });
  });
  startRoot(root);
};

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-mystery-availability-root]').forEach(initializeRoot);

  document.addEventListener('sigma:collection-view:updating', (event) => {
    const root = event.target.closest?.('[data-mystery-availability-root]')
      || event.detail?.collection?.closest?.('[data-mystery-availability-root]')
      || event.detail?.view?.closest?.('[data-mystery-availability-root]');
    if (root instanceof HTMLElement) rootStates.get(root)?.controller.abort();
  });

  document.addEventListener('sigma:collection-view:updated', (event) => {
    const root = event.target.closest?.('[data-mystery-availability-root]')
      || event.detail?.collection?.closest?.('[data-mystery-availability-root]')
      || event.detail?.view?.closest?.('[data-mystery-availability-root]');
    if (!(root instanceof HTMLElement)) return;
    if (event.detail?.nextCollection || rootStates.get(root)?.controller.signal.aborted) {
      startRoot(root);
    } else {
      hydrateNewCards(root);
    }
  });

  new MutationObserver(() => {
    activeRoots.forEach((root) => {
      if (root.isConnected) return;
      rootStates.get(root)?.controller.abort();
      activeRoots.delete(root);
    });
    document.querySelectorAll('[data-mystery-availability-root]').forEach(initializeRoot);
    activeRoots.forEach(hydrateNewCards);
  }).observe(document.documentElement, { childList: true, subtree: true });
}
